import React, { useState, useEffect } from 'react';
import {
  Alert,
  InputGroup,
  InputGroupText,
  Input,
  Button,
  Card,
  CardHeader,
  CardBody,
  Progress,
  Badge
} from 'reactstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { addContentExtra, updateContentExtra, processContentChaptersAudio } from '../../helpers/API/contentExtra';
import i18n from '../../i18n';
import { appLanguages } from '../../common/languages';
import { getVoicesList } from '../../helpers/API/content/voices';
import { NEVILLE_GODDARD_VOICE, isNevilleGoddardVoiceSupported } from '../../constants/nevilleGoddard';

// Helper to split long text into chapters (each chapter ≤9800 characters)
const splitTextIntoChapters = (text, maxChars = 9800) => {
  const paragraphs = text.split('\n').filter(p => p.trim() !== '');
  const chapters = [];
  let currentChapter = [];
  let currentLength = 0;
  paragraphs.forEach(para => {
    const paraTrimmed = para.trim();
    if (currentLength + paraTrimmed.length + 1 > maxChars && currentChapter.length > 0) {
      chapters.push(currentChapter);
      currentChapter = [paraTrimmed];
      currentLength = paraTrimmed.length + 1;
    } else {
      currentChapter.push(paraTrimmed);
      currentLength += paraTrimmed.length + 1;
    }
  });
  if (currentChapter.length > 0) {
    chapters.push(currentChapter);
  }
  return chapters;
};

// Form Field Component (moved outside to prevent recreation and focus loss)
const FormField = React.memo(({ id, label, type = 'text', placeholder = '', disabled = false, isSelect = false, options = [], helpText = '', required = false, formik, styles }) => (
  <div style={styles.formGroup}>
    <label style={styles.label} htmlFor={id}>
      {label}
      {required && <span style={styles.required}>*</span>}
    </label>
    {helpText && <p style={styles.helpText}>{helpText}</p>}

    {isSelect ? (
      <select
        id={id}
        name={id}
        value={formik.values[id]}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        disabled={disabled}
        style={{
          ...styles.input,
          ...(formik.touched[id] && formik.errors[id] ? styles.inputError : {}),
          ...(disabled ? styles.inputDisabled : {})
        }}
      >
        <option value="">{placeholder}</option>
        {options.map((option, index) => (
          <option key={index} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    ) : (
      <input
        id={id}
        name={id}
        type={type}
        placeholder={placeholder}
        disabled={disabled}
        value={formik.values[id]}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        style={{
          ...styles.input,
          ...(formik.touched[id] && formik.errors[id] ? styles.inputError : {}),
          ...(disabled ? styles.inputDisabled : {})
        }}
      />
    )}

    {formik.touched[id] && formik.errors[id] && (
      <div style={styles.errorText}>
        <i className="bx bx-error-circle me-1"></i>
        {formik.errors[id]}
      </div>
    )}
  </div>
));

FormField.displayName = 'FormField';

// Textarea Field Component (moved outside to prevent recreation and focus loss)
const TextareaField = React.memo(({ id, label, placeholder = '', rows = 4, helpText = '', required = false, formik, styles }) => (
  <div style={styles.formGroup}>
    <label style={styles.label} htmlFor={id}>
      {label}
      {required && <span style={styles.required}>*</span>}
    </label>
    {helpText && <p style={styles.helpText}>{helpText}</p>}

    <textarea
      id={id}
      name={id}
      placeholder={placeholder}
      rows={rows}
      value={formik.values[id]}
      onChange={formik.handleChange}
      onBlur={formik.handleBlur}
      style={{
        ...styles.textarea,
        ...(formik.touched[id] && formik.errors[id] ? styles.inputError : {})
      }}
    />

    {formik.touched[id] && formik.errors[id] && (
      <div style={styles.errorText}>
        <i className="bx bx-error-circle me-1"></i>
        {formik.errors[id]}
      </div>
    )}
  </div>
));

TextareaField.displayName = 'TextareaField';

const NewContentForm = ({ section, language, voices: parentVoices = [], onFinish, editingItem }) => {
  const initialContentType = editingItem ? editingItem.type : (section || 'podcasts');
  const [errorMessage, setErrorMessage] = useState('');
  const [isGeneratingAudio, setIsGeneratingAudio] = useState(false);

  const initialValues = {
    contentType: initialContentType,
    title: editingItem ? editingItem.title : '',
    imageUrl: editingItem ? editingItem.imageUrl : '',
    text: editingItem
      ? (editingItem.text && Array.isArray(editingItem.text)
          ? editingItem.text.join('\n')
          : editingItem.fullText || '') // Fallback for old data
      : '',
    voice: editingItem ? editingItem.voice : '',
    author: editingItem ? editingItem.author : 'Neville Goddard',
    lang: editingItem ? editingItem.lang : language,
  };

  const validationSchema = Yup.object({
    contentType: Yup.string().required(i18n.t('required')),
    title: Yup.string().required(i18n.t('required')),
    imageUrl: Yup.string().url(i18n.t('invalidURL')).required(i18n.t('required')),
    text: Yup.string().required(i18n.t('required')),
    lang: Yup.string().required(i18n.t('required')),
    voice: Yup.string().when('contentType', {
      is: (val) => val === 'lectures' || val === 'books',
      then: Yup.string().required(i18n.t('required')),
      otherwise: Yup.string().notRequired()
    }),
    author: Yup.string().when('contentType', {
      is: (val) => val === 'lectures' || val === 'books',
      then: Yup.string().required(i18n.t('required')),
      otherwise: Yup.string().notRequired()
    }),
  });

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      setIsGeneratingAudio(true);

      let contentData = {
        title: values.title,
        imageUrl: values.imageUrl,
        lang: values.lang,
        type: values.contentType,
        createdAt: new Date().toISOString(),
      };

      if (values.contentType === 'lectures' || values.contentType === 'books') {
        // For lectures/books, store text as array in main document
        // Chapters will be created in subcollections during processing
        contentData = {
          ...contentData,
          text: values.text.split('\n').filter(line => line.trim()), // Remove empty lines
          voice: values.voice,
          author: values.author,
        };
      } else {
        // For podcasts, use text array format
        contentData = {
          ...contentData,
          text: values.text.split('\n').filter(line => line.trim())
        };
      }

      try {
        let result;

        if (editingItem) {
          await updateContentExtra(values.contentType, values.lang, editingItem.id, contentData);
          result = { id: editingItem.id, ...contentData };
        } else {
          result = await addContentExtra(values.contentType, values.lang, contentData);
        }

        // Note: For lectures/books, chapters are now created manually via the UI
        // Audio generation happens when each chapter is added individually
        console.log(`✅ ${values.contentType} content created successfully with text array format`);

        resetForm();
        if (onFinish) onFinish();

      } catch (error) {
        console.error("Error adding/updating content:", error);
        setErrorMessage(i18n.t("failedToAddContent"));
      } finally {
        setSubmitting(false);
        setIsGeneratingAudio(false);
      }
    },
    validateOnBlur: true,
    validateOnChange: true,
  });

  const isLongContent = formik.values.contentType === 'lectures' || formik.values.contentType === 'books';
  const [formVoices, setFormVoices] = useState(parentVoices);

  useEffect(() => {
    const fetchFormVoices = async () => {
      try {
        const data = await getVoicesList(formik.values.lang);
        const elevenLabsVoices = data.filter(voice => voice.type === 'elevenLabs');
        setFormVoices(elevenLabsVoices);
      } catch (error) {
        console.error('Error fetching voices for form:', error);
      }
    };
    fetchFormVoices();
  }, [formik.values.lang]);

  useEffect(() => {
    if (isLongContent && formVoices.length > 0 && !formik.values.voice) {
      const nevilleVoice = formVoices.find(voice =>
        voice.name === NEVILLE_GODDARD_VOICE.id || voice.displayName === NEVILLE_GODDARD_VOICE.displayName
      );

      if (nevilleVoice && isNevilleGoddardVoiceSupported(formik.values.lang)) {
        formik.setFieldValue('voice', nevilleVoice.name);
      }
    }
  }, [isLongContent, formVoices, formik.values.voice, formik.values.lang]);

  const charCount = formik.values.text.length;

  const getContentTypeColors = (type) => {
    const colorSchemes = {
      podcasts: {
        gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        light: '#667eea',
        dark: '#764ba2'
      },
      lectures: {
        gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
        light: '#f093fb',
        dark: '#f5576c'
      },
      books: {
        gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        light: '#4facfe',
        dark: '#00f2fe'
      }
    };
    return colorSchemes[type] || colorSchemes.lectures;
  };

  const colors = getContentTypeColors(formik.values.contentType);

  return (
    <div style={styles.container}>
      <Card style={styles.formCard}>
        <CardHeader style={{...styles.cardHeader, background: colors.gradient}}>
          <div style={styles.headerContent}>
            <div style={styles.headerIcon}>
              <i className={`bx ${editingItem ? 'bx-edit' : 'bx-plus-circle'}`}></i>
            </div>
            <div>
              <h4 style={styles.headerTitle}>
                {editingItem ? i18n.t('updateContent') : i18n.t('addContent')}
              </h4>
              <p style={styles.headerSubtitle}>
                {editingItem ? 'Update your content details' : 'Create amazing content with Neville Goddard\'s voice'}
              </p>
            </div>
          </div>
        </CardHeader>

        <CardBody style={styles.cardBody}>
          <form onSubmit={formik.handleSubmit}>
            <div style={styles.section}>
              <h5 style={styles.sectionTitle}>
                <i className="bx bx-category me-2"></i>
                Content Information
              </h5>

              <FormField
                id="contentType"
                label={i18n.t('contentType')}
                isSelect={true}
                placeholder={i18n.t('selectContentType')}
                required={true}
                options={[
                  { value: 'podcasts', label: i18n.t('podcast') },
                  { value: 'lectures', label: i18n.t('lecture') },
                  { value: 'books', label: i18n.t('book') }
                ]}
                formik={formik}
                styles={styles}
              />

              <FormField
                id="title"
                label={i18n.t('title')}
                placeholder={i18n.t('enterTitle')}
                required={true}
                formik={formik}
                styles={styles}
              />

              <FormField
                id="imageUrl"
                label={i18n.t('imageURL')}
                type="url"
                placeholder={i18n.t('enterImageUrl')}
                required={true}
                helpText="Provide a high-quality image URL for your content"
                formik={formik}
                styles={styles}
              />

              <FormField
                id="lang"
                label={i18n.t('language')}
                isSelect={true}
                placeholder={i18n.t('selectLanguage')}
                required={true}
                options={appLanguages.map(lang => ({
                  value: lang,
                  label: lang.toUpperCase()
                }))}
                formik={formik}
                styles={styles}
              />
            </div>

            {isLongContent && (
              <div style={styles.section}>
                <h5 style={styles.sectionTitle}>
                  <i className="bx bx-book-open me-2"></i>
                  {formik.values.contentType === 'lectures' ? 'Lecture' : 'Book'} Details
                </h5>

                {!isNevilleGoddardVoiceSupported(formik.values.lang) && (
                  <Alert color="warning" style={styles.warningAlert}>
                    <i className="bx bx-info-circle me-2"></i>
                    <strong>Voice Limitation:</strong> Neville Goddard voice is not supported for {formik.values.lang.toUpperCase()}.
                    Supported languages: {NEVILLE_GODDARD_VOICE.supportedLanguages.map(lang => lang.toUpperCase()).join(', ')}
                  </Alert>
                )}

                <FormField
                  id="author"
                  label={i18n.t('author')}
                  placeholder={i18n.t('enterAuthor')}
                  required={true}
                  formik={formik}
                  styles={styles}
                />

                <TextareaField
                  id="text"
                  label={i18n.t('content')}
                  placeholder={i18n.t('pasteFullContentHere')}
                  rows={12}
                  required={true}
                  helpText="Paste the complete text. It will be automatically split into chapters."
                  formik={formik}
                  styles={styles}
                />

                {charCount > 0 && (
                  <div style={styles.statsContainer}>
                    <div style={styles.statItem}>
                      <div style={styles.statNumber}>{charCount.toLocaleString()}</div>
                      <div style={styles.statLabel}>Characters</div>
                    </div>
                    <div style={styles.statItem}>
                      <div style={styles.statNumber}>~{Math.ceil(charCount / 1000)}</div>
                      <div style={styles.statLabel}>Minutes</div>
                    </div>
                    <div style={styles.statItem}>
                      <div style={styles.statNumber}>📖</div>
                      <div style={styles.statLabel}>Chapters Added Later</div>
                    </div>
                  </div>
                )}

                <FormField
                  id="voice"
                  label={i18n.t('voice')}
                  isSelect={true}
                  placeholder={i18n.t('selectVoice')}
                  disabled={!isNevilleGoddardVoiceSupported(formik.values.lang)}
                  required={true}
                  options={formVoices.map(voice => ({
                    value: voice.name,
                    label: `${voice.displayName}${voice.displayName === NEVILLE_GODDARD_VOICE.displayName ? ' (Recommended)' : ''}`
                  }))}
                  helpText="The selected voice will be used to generate audio for all chapters"
                  formik={formik}
                  styles={styles}
                />
              </div>
            )}

            {!isLongContent && (
              <div style={styles.section}>
                <h5 style={styles.sectionTitle}>
                  <i className="bx bx-microphone me-2"></i>
                  Podcast Content
                </h5>

                <TextareaField
                  id="text"
                  label={i18n.t('text')}
                  placeholder={i18n.t('enterContentText')}
                  rows={8}
                  required={true}
                  helpText="Enter your podcast content. Each line will be treated as a separate paragraph."
                  formik={formik}
                  styles={styles}
                />
              </div>
            )}

            <div style={styles.submitSection}>
              <Button
                type="submit"
                disabled={!formik.dirty || !formik.isValid || formik.isSubmitting || isGeneratingAudio}
                style={{
                  ...styles.submitButton,
                  background: colors.gradient
                }}
              >
                {isGeneratingAudio ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    {i18n.t('generatingAudio')}...
                  </>
                ) : (
                  <>
                    <i className={`bx ${editingItem ? 'bx-check' : 'bx-plus'} me-2`}></i>
                    {editingItem ? i18n.t('updateContent') : i18n.t('addContent')}
                  </>
                )}
              </Button>

              {onFinish && (
                <Button
                  type="button"
                  onClick={onFinish}
                  style={styles.cancelButton}
                  disabled={isGeneratingAudio}
                >
                  <i className="bx bx-x me-2"></i>
                  {i18n.t('cancel')}
                </Button>
              )}
            </div>

            {errorMessage && (
              <Alert color="danger" style={styles.errorAlert}>
                <i className="bx bx-error-circle me-2"></i>
                {errorMessage}
              </Alert>
            )}

            {isGeneratingAudio && (
              <Alert color="info" style={styles.infoAlert}>
                <div style={styles.progressContainer}>
                  <div style={styles.progressInfo}>
                    <i className="bx bx-loader-alt bx-spin me-2"></i>
                    <span>{i18n.t('audioGenerationInProgress')}</span>
                  </div>
                  <Progress animated color="info" value={100} style={styles.progressBar} />
                  <small style={styles.progressText}>
                    This may take several minutes depending on content length...
                  </small>
                </div>
              </Alert>
            )}
          </form>
        </CardBody>
      </Card>
    </div>
  );
};

// Modern Styles
const styles = {
  container: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '20px',
  },
  formCard: {
    border: 'none',
    borderRadius: '20px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden',
  },
  cardHeader: {
    border: 'none',
    padding: '32px',
    color: 'white',
  },
  headerContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
  },
  headerIcon: {
    width: '60px',
    height: '60px',
    borderRadius: '16px',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.8rem',
    backdropFilter: 'blur(10px)',
  },
  headerTitle: {
    margin: '0 0 8px 0',
    fontSize: '1.8rem',
    fontWeight: '700',
    color: 'white',
  },
  headerSubtitle: {
    margin: 0,
    fontSize: '1rem',
    color: 'rgba(255, 255, 255, 0.9)',
    fontWeight: '400',
  },
  cardBody: {
    padding: '40px',
    backgroundColor: '#fafbfc',
  },
  section: {
    marginBottom: '40px',
    padding: '24px',
    backgroundColor: 'white',
    borderRadius: '16px',
    border: '1px solid #e2e8f0',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
  },
  sectionTitle: {
    margin: '0 0 24px 0',
    fontSize: '1.25rem',
    fontWeight: '600',
    color: '#2d3748',
    display: 'flex',
    alignItems: 'center',
    paddingBottom: '12px',
    borderBottom: '2px solid #e2e8f0',
  },
  formGroup: {
    marginBottom: '24px',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontSize: '0.95rem',
    fontWeight: '600',
    color: '#4a5568',
  },
  required: {
    color: '#e53e3e',
    marginLeft: '4px',
  },
  helpText: {
    marginBottom: '8px',
    fontSize: '0.85rem',
    color: '#718096',
    fontStyle: 'italic',
  },
  input: {
    width: '100%',
    padding: '14px 16px',
    fontSize: '1rem',
    border: '2px solid #e2e8f0',
    borderRadius: '12px',
    backgroundColor: '#fafbfc',
    transition: 'all 0.2s ease',
    outline: 'none',
  },
  textarea: {
    width: '100%',
    padding: '14px 16px',
    fontSize: '1rem',
    border: '2px solid #e2e8f0',
    borderRadius: '12px',
    backgroundColor: '#fafbfc',
    transition: 'all 0.2s ease',
    outline: 'none',
    resize: 'vertical',
    fontFamily: 'inherit',
  },
  inputError: {
    borderColor: '#e53e3e',
    backgroundColor: '#fed7d7',
  },
  inputDisabled: {
    backgroundColor: '#f7fafc',
    color: '#a0aec0',
    cursor: 'not-allowed',
  },
  errorText: {
    marginTop: '8px',
    fontSize: '0.875rem',
    color: '#e53e3e',
    display: 'flex',
    alignItems: 'center',
  },
  statsContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
    gap: '16px',
    margin: '16px 0',
    padding: '20px',
    backgroundColor: '#f7fafc',
    borderRadius: '12px',
    border: '1px solid #e2e8f0',
  },
  statItem: {
    textAlign: 'center',
  },
  statNumber: {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: '#2d3748',
    display: 'block',
  },
  statLabel: {
    fontSize: '0.875rem',
    color: '#718096',
    marginTop: '4px',
  },
  submitSection: {
    display: 'flex',
    gap: '16px',
    marginTop: '32px',
    paddingTop: '24px',
    borderTop: '1px solid #e2e8f0',
  },
  submitButton: {
    flex: 1,
    padding: '16px 24px',
    fontSize: '1.1rem',
    fontWeight: '600',
    border: 'none',
    borderRadius: '12px',
    color: 'white',
    transition: 'all 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  cancelButton: {
    padding: '16px 24px',
    fontSize: '1.1rem',
    fontWeight: '600',
    border: '2px solid #e2e8f0',
    borderRadius: '12px',
    backgroundColor: 'white',
    color: '#4a5568',
    transition: 'all 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  warningAlert: {
    borderRadius: '12px',
    border: '1px solid #ed8936',
    backgroundColor: '#fffaf0',
    padding: '16px',
    marginBottom: '20px',
  },
  errorAlert: {
    borderRadius: '12px',
    border: '1px solid #e53e3e',
    backgroundColor: '#fed7d7',
    padding: '16px',
    marginTop: '20px',
  },
  infoAlert: {
    borderRadius: '12px',
    border: '1px solid #3182ce',
    backgroundColor: '#ebf8ff',
    padding: '20px',
    marginTop: '20px',
  },
  progressContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  progressInfo: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '1rem',
    fontWeight: '500',
    color: '#2b6cb0',
  },
  progressBar: {
    height: '8px',
    borderRadius: '4px',
  },
  progressText: {
    color: '#4a5568',
    fontSize: '0.875rem',
  },
};

export default NewContentForm;